/**
 * Shared render primitive for every standalone (Preact + htm) visualization preview in the Portal.
 *
 * It fetches the authenticated, self-contained HTML document for ONE submission (the submission payload
 * is inlined into the doc by the API) and renders it INERT inside a sandboxed `srcdoc` iframe. Because the
 * payload is inlined there is no in-iframe data fetch, so we deliberately OMIT `allow-same-origin` — the
 * iframe keeps an opaque ("null") origin: no cookies, no localStorage, no parent-DOM access, no credentialed
 * fetch. The external kit `<script src>` still loads. `allow-popups`/`allow-downloads` cover a document link.
 *
 * Auto-height: the kit posts `{ type:'aqrate.viz.resize', height }`. We can't validate `event.origin`
 * (sandboxed srcdoc reports origin "null"), so we trust messages ONLY when `event.source` is this iframe's
 * contentWindow AND the message shape matches. Thumbnail mode renders at a fixed logical size, scaled to fit
 * a small card, and ignores resize messages.
 *
 * @param {{
 *   submissionId: number,
 *   vizId?: number,
 *   refreshKey?: any,
 *   minHeight?: number,
 *   maxHeight?: number,
 *   fitContent?: boolean,
 *   thumbnail?: boolean,
 *   title?: string,
 * }} props
 */

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import standaloneVizService from '../../api/services/standaloneVizService';

/**
 * Fetch a submission's self-contained render doc and open it in a new tab via a transient blob URL.
 * The doc is self-contained (payload inlined), so the opened tab needs no auth. Shared by the
 * submission card's "Open ↗" and the editor's "Open in new tab" so the blob lifecycle lives in one place.
 * @param {number} submissionId
 * @param {number} [vizId]
 * @returns {Promise<boolean>} true if a tab was opened
 */
export async function openVizInNewTab(submissionId, vizId) {
  const html = await standaloneVizService.getRenderDoc(submissionId, vizId);
  return openDocInNewTab(html);
}

/**
 * List-viz equivalent of {@link openVizInNewTab}: fetch a ListView viz's self-contained doc (over the saved
 * config, or a launch-time filter override) and open it in a new tab via a transient blob URL.
 * @param {number} formSchemaId @param {number} vizId @param {object} [filterOverride]
 * @returns {Promise<boolean>} true if a tab was opened
 */
export async function openVizListInNewTab(formSchemaId, vizId, filterOverride) {
  const html = await standaloneVizService.getRenderListDoc(formSchemaId, vizId, filterOverride);
  return openDocInNewTab(html);
}

/** Open a self-contained HTML doc in a new tab via a transient blob URL (revoked shortly after). */
function openDocInNewTab(html) {
  if (!html) return false;
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  window.open(url, '_blank', 'noopener');
  // The new tab keeps its own copy of the doc; revoke shortly after to free memory.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
}

// Fixed logical canvas the thumbnail renders at before scaling down (matches the manager's old look).
const THUMB_LOGICAL_WIDTH = 900;
const THUMB_LOGICAL_HEIGHT = 560;
const THUMB_DISPLAY_HEIGHT = 140;
const THUMB_SCALE = 0.4; // 900 * 0.4 = 360px wide thumbnail content

export function StandaloneVizFrame({
  submissionId,
  vizId,
  refreshKey,
  minHeight = 160,
  maxHeight = 900,
  fitContent = false,
  thumbnail = false,
  title,
  mode = 'submission',
  formSchemaId,
  filterOverride,
  onRenderError,
}) {
  const theme = useTheme();
  const iframeRef = useRef(null);
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [height, setHeight] = useState(minHeight);
  const isList = mode === 'list';
  // Stabilize the override object across renders so the effect doesn't refetch on every parent render.
  const overrideKey = filterOverride ? JSON.stringify(filterOverride) : '';

  // ── Fetch the self-contained doc on mount and whenever the inputs change ────
  useEffect(() => {
    // Submission mode needs a submissionId; list mode needs a formSchemaId + vizId.
    if ((isList && (!formSchemaId || !vizId)) || (!isList && !submissionId)) {
      setHtml(null);
      setLoading(false);
      setError(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    const fetchDoc = isList
      ? standaloneVizService.getRenderListDoc(formSchemaId, vizId, filterOverride || undefined)
      : standaloneVizService.getRenderDoc(submissionId, vizId);
    fetchDoc
      .then((doc) => {
        if (cancelled) return;
        setHtml(typeof doc === 'string' ? doc : '');
        setHeight(minHeight);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setHtml(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isList, formSchemaId, submissionId, vizId, overrideKey, refreshKey]);

  // ── Auto-height from the kit's postMessage (skipped entirely in thumbnail mode) ──
  useEffect(() => {
    if (thumbnail) return undefined;
    const onMessage = (e) => {
      const frame = iframeRef.current;
      if (!frame || e.source !== frame.contentWindow) return; // only trust our own iframe
      const data = e.data;
      if (!data || typeof data.type !== 'string') return;
      // The authored view threw while rendering — surface the real error to the host (e.g. the editor's
      // "Fix with AI" banner). Mirrors the kit's aqrate.viz.error channel.
      if (data.type === 'aqrate.viz.error') {
        if (onRenderError) onRenderError({ message: data.message || 'The visualization failed to render.', stack: data.stack || '' });
        return;
      }
      if (data.type !== 'aqrate.viz.resize' || typeof data.height !== 'number') return;
      // fitContent: grow to the doc's full height (the outer pane scrolls). Otherwise clamp to maxHeight.
      const clamped = fitContent
        ? Math.max(data.height, minHeight)
        : Math.min(Math.max(data.height, minHeight), maxHeight);
      setHeight(clamped);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [thumbnail, minHeight, maxHeight, fitContent, onRenderError]);

  // ── Thumbnail: fixed logical canvas scaled to fit a small, clipped card ─────
  if (thumbnail) {
    if (loading || error || !html) {
      return <ThumbPlaceholder theme={theme} aria-hidden />;
    }
    return (
      <ThumbWrap theme={theme}>
        <ThumbScaler>
          <iframe
            ref={iframeRef}
            srcDoc={html}
            sandbox="allow-scripts allow-popups allow-downloads"
            scrolling="no"
            title={title || 'Visualization'}
            width={THUMB_LOGICAL_WIDTH}
            height={THUMB_LOGICAL_HEIGHT}
            style={{ border: 'none', display: 'block', background: '#fff' }}
          />
        </ThumbScaler>
      </ThumbWrap>
    );
  }

  if (loading) {
    return <Placeholder theme={theme} style={{ minHeight }}>Loading visualization…</Placeholder>;
  }
  if (error) {
    return <Placeholder theme={theme} style={{ minHeight }}>Preview unavailable.</Placeholder>;
  }
  if (!html) {
    return <Placeholder theme={theme} style={{ minHeight }}>No visualization to preview.</Placeholder>;
  }

  return (
    <Frame
      ref={iframeRef}
      theme={theme}
      srcDoc={html}
      sandbox="allow-scripts allow-popups allow-downloads"
      scrolling="no"
      title={title || 'Visualization'}
      style={{ height: `${height}px` }}
    />
  );
}

const Frame = styled.iframe`
  width: 100%;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  display: block;
  background: #fff;
`;

const Placeholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-align: center;
  color: ${p => p.theme.colors.muted};
  font-size: 13px;
  border: 1px dashed ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.background};
  padding: ${p => p.theme.spacing.lg};
`;

const ThumbWrap = styled.div`
  position: relative;
  width: 100%;
  height: ${THUMB_DISPLAY_HEIGHT}px;
  overflow: hidden;
  background: #fff;
`;

// Scale the fixed-width logical canvas down so a full layout reads as a thumbnail.
const ThumbScaler = styled.div`
  transform: scale(${THUMB_SCALE});
  transform-origin: top left;
  pointer-events: none;
  width: ${THUMB_LOGICAL_WIDTH}px;
  height: ${THUMB_LOGICAL_HEIGHT}px;
`;

const ThumbPlaceholder = styled.div`
  width: 100%;
  height: ${THUMB_DISPLAY_HEIGHT}px;
  background: ${p => `${p.theme.colors.tint}12`};
`;

export default StandaloneVizFrame;
